import React, { useState } from 'react';
import { X, Mail, Lock, Loader2, ArrowLeft, Building2, Users, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';
import { useAuth } from '../contexts/AuthContext';
import PasswordStrengthMeter from './PasswordStrengthMeter';

const EmailFirstAuthModal = ({ isOpen, onClose, onSuccess }) => {
  const { tierLimits } = useAuth();
  const [step, setStep] = useState('email'); // 'email', 'password', 'forgot-password', 'reset-sent', 'verification-sent'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [showPromoCode, setShowPromoCode] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [orgInfo, setOrgInfo] = useState(null); // { org_name, org_id, member_count }
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  // Check URL for promo code parameter
  React.useEffect(() => {
    if (isOpen && !isExistingUser) {
      const params = new URLSearchParams(window.location.search);
      const promoParam = params.get('promo');
      if (promoParam) {
        setPromoCode(promoParam);
        setShowPromoCode(true);
      }
    }
  }, [isOpen, isExistingUser]);

  const resetForm = () => {
    setStep('email');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setPromoCode('');
    setShowPromoCode(false);
    setIsExistingUser(false);
    setOrgInfo(null);
    setError('');
    setResendLoading(false);
    setResendMessage('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_ENDPOINTS.main}/auth/check-email`, { email });
      const { exists, org_name, org_id, member_count } = response.data;

      setIsExistingUser(exists);

      if (!exists && org_name) {
        // New user with org match
        setOrgInfo({ org_name, org_id, member_count });
      }

      setStep('password');
    } catch (err) {
      setError('Failed to verify email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (!password) {
      setError('Password is required');
      return;
    }

    if (!isExistingUser) {
      // Signup validation
      if (password.length < 8) {
        setError('Password must be at least 8 characters');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    setIsLoading(true);
    setError('');
    setResendMessage('');

    try {
      const endpoint = isExistingUser ? '/auth/login' : '/auth/register';
      const payload = {
        email,
        password
      };

      // Include promo code for registration only
      if (!isExistingUser && promoCode) {
        payload.promo_code = promoCode.trim().toUpperCase();
      }

      const response = await axios.post(`${API_ENDPOINTS.main}${endpoint}`, payload);

      if (isExistingUser) {
        // Login flow - save token and reload
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('userId', response.data.user_id);
        localStorage.setItem('userEmail', response.data.email);

        handleClose();
        if (onSuccess) {
          onSuccess(response.data);
        }

        // Reload page to trigger AuthContext to pick up new token
        window.location.reload();
      } else {
        // Registration flow - show verification sent message
        setStep('verification-sent');
      }
    } catch (err) {
      // Handle rate limiting (429) with specific message
      if (err.response?.status === 429) {
        setError('Too many login attempts. Please try again in 15 minutes.');
      } else {
        const errorMessage = err.response?.data?.detail || 'Authentication failed';
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await axios.post(`${API_ENDPOINTS.main}/auth/forgot-password`, { email });
      setStep('reset-sent');
    } catch (err) {
      // Handle rate limiting (429) with specific message
      if (err.response?.status === 429) {
        setError('Too many password reset attempts. Please try again in 15 minutes.');
      } else {
        // Always show success to avoid email enumeration
        setStep('reset-sent');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setPassword('');
    setConfirmPassword('');
    setIsExistingUser(false);
    setOrgInfo(null);
    setError('');
    setResendMessage('');
    setStep('email');
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMessage('');

    try {
      await axios.post(`${API_ENDPOINTS.main}/auth/resend-verification`, { email });
      setResendMessage('Verification email sent! Check your inbox.');
    } catch (err) {
      // Handle rate limiting (429) with specific message
      if (err.response?.status === 429) {
        setResendMessage('Too many requests. Please try again in 15 minutes.');
      } else {
        setResendMessage('Failed to resend email. Please try again.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full max-h-[calc(100vh-2rem)] overflow-hidden animate-in zoom-in-95 duration-200 border border-gray-200 dark:border-gray-700 flex flex-col">

        {/* Header */}
        <div className="relative p-5 text-center border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
          >
            <X size={20} />
          </button>

          <div className="mb-3">
            <div className="mx-auto w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-3 shadow-lg">
              <Mail className="h-7 w-7 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {step === 'forgot-password' ? 'Reset password' :
               step === 'reset-sent' ? 'Check your email' :
               step === 'verification-sent' ? 'Account created!' :
               'Ready to analyze your data?'}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {step === 'email' ? 'Enter your email to get started with VerbaGPT' :
               step === 'forgot-password' ? 'Enter your email to receive a reset link' :
               step === 'reset-sent' ? 'If an account exists, we sent a password reset link' :
               step === 'verification-sent' ? 'Please verify your email to continue' :
               isExistingUser ? 'Welcome back!' :
               orgInfo ? `Join ${orgInfo.org_name}` :
               'Create your account'}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Error Message */}
          {error && (
            <div className="mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 animate-in slide-in-from-top-1 duration-300">
              <p className="text-red-600 dark:text-red-400 text-xs font-medium">{error}</p>
              {error.toLowerCase().includes('verify your email') && (
                <div className="mt-2 flex items-center gap-2">
                  {resendMessage && (
                    <span className={`text-xs ${resendMessage.includes('sent') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {resendMessage}
                    </span>
                  )}
                  <button
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className={`text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed font-medium ${resendMessage ? 'ml-auto' : ''}`}
                  >
                    {resendLoading ? 'Sending...' : 'Resend email'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 1: Email Entry */}
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit} className="space-y-5">
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError('');
                    }}
                    className="block w-full pl-12 pr-4 py-3.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white placeholder-gray-400 transition-all duration-200 text-sm"
                    placeholder="email@example.com"
                    disabled={isLoading}
                    autoFocus
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                    Checking...
                  </>
                ) : (
                  'Continue →'
                )}
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-medium">or</span>
                </div>
              </div>

              {/* Google Sign-In Button */}
              <button
                type="button"
                onClick={() => {
                  window.location.href = `${API_ENDPOINTS.main}/auth/google`;
                }}
                disabled={isLoading}
                className="w-full bg-white dark:bg-gray-700 border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-200 font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center shadow-sm hover:shadow-md hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
            </form>
          )}

          {/* Step 2: Password (Login or Signup) */}
          {step === 'password' && (
            <form onSubmit={handlePasswordSubmit} className="space-y-3">
              {/* Show email */}
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-3">
                <Mail className="h-4 w-4" />
                <span>{email}</span>
                <button
                  type="button"
                  onClick={handleBackToEmail}
                  className="ml-auto text-blue-600 hover:text-blue-500 dark:text-blue-400 hover:underline flex items-center gap-1"
                >
                  <ArrowLeft className="h-3 w-3" />
                  Change
                </button>
              </div>

              {/* Org info banner for new users joining org */}
              {!isExistingUser && orgInfo && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3 mb-3 animate-in slide-in-from-top-1 duration-300">
                  <div className="flex items-start gap-3">
                    <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                        Join {orgInfo.org_name}
                      </p>
                      <p className="text-xs text-blue-700 dark:text-blue-300 mt-1 flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {orgInfo.member_count} {orgInfo.member_count === 1 ? 'member' : 'members'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Plan info for individual users */}
              {!isExistingUser && !orgInfo && (
                <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl p-3 mb-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Individual Free Plan • {tierLimits?.free?.credits_per_month || 50} queries/month
                  </p>
                </div>
              )}

              {/* Password */}
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {isExistingUser ? 'Password' : 'Create password'}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError('');
                      if (resendMessage) setResendMessage('');
                    }}
                    className="block w-full pl-12 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white placeholder-gray-400 transition-all duration-200 text-sm"
                    placeholder={isExistingUser ? 'Enter your password' : 'At least 8 characters'}
                    disabled={isLoading}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {/* Password strength meter for signup */}
                {!isExistingUser && <PasswordStrengthMeter password={password} />}
              </div>

              {/* Confirm password for signup */}
              {!isExistingUser && (
                <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
                  <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Confirm password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (error) setError('');
                        if (resendMessage) setResendMessage('');
                      }}
                      className="block w-full pl-12 pr-12 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white placeholder-gray-400 transition-all duration-200 text-sm"
                      placeholder="Confirm your password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Promo Code Field (Signup only, collapsible) */}
              {!isExistingUser && (
                <div className="animate-in slide-in-from-top-2 duration-300">
                  {!showPromoCode ? (
                    <button
                      type="button"
                      onClick={() => setShowPromoCode(true)}
                      className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 font-medium hover:underline"
                      disabled={isLoading}
                    >
                      Have a promo code?
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <label htmlFor="promoCode" className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Promo code
                      </label>
                      <input
                        type="text"
                        id="promoCode"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          if (error) setError('');
                          if (resendMessage) setResendMessage('');
                        }}
                        className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white placeholder-gray-400 transition-all duration-200 text-sm uppercase"
                        placeholder="Enter promo code"
                        disabled={isLoading}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Forgot password link for login */}
              {isExistingUser && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setStep('forgot-password')}
                    className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 hover:underline"
                    disabled={isLoading}
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3.5 px-6 rounded-xl transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                    {isExistingUser ? 'Signing in...' : orgInfo ? 'Joining...' : 'Creating account...'}
                  </>
                ) : (
                  isExistingUser ? 'Sign In' : orgInfo ? `Join ${orgInfo.org_name}` : 'Sign Up'
                )}
              </button>
            </form>
          )}

          {/* Step 3: Forgot Password */}
          {step === 'forgot-password' && (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                We'll send a password reset link to your email address.
              </p>

              <div className="space-y-2">
                <label htmlFor="reset-email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="reset-email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError('');
                    }}
                    className="block w-full pl-12 pr-4 py-3.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 dark:bg-gray-700 dark:text-white placeholder-gray-400 transition-all duration-200 text-sm"
                    placeholder="email@example.com"
                    disabled={isLoading}
                    autoFocus
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setStep('password')}
                  className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 hover:underline flex items-center gap-1 mx-auto"
                >
                  <ArrowLeft className="h-3 w-3" />
                  Back to sign in
                </button>
              </div>
            </form>
          )}

          {/* Step 4: Reset Link Sent */}
          {step === 'reset-sent' && (
            <div className="text-center space-y-6 py-8">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <Mail className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-gray-700 dark:text-gray-300 mb-2">
                  If an account with <strong>{email}</strong> exists, we've sent a password reset link.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Check your inbox and click the link to reset your password.
                </p>
              </div>
              <button
                onClick={handleClose}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
              >
                Close
              </button>
            </div>
          )}

          {/* Step: Verification Email Sent */}
          {step === 'verification-sent' && (
            <div className="text-center space-y-4 py-4">
              <div className="mx-auto w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <Mail className="h-7 w-7 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Verify your email
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                  We sent a verification link to <strong>{email}</strong>
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Click the link to activate your account and start analyzing data
                </p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-xs text-blue-800 dark:text-blue-300">
                  Check spam folder • Link expires in 24 hours
                </p>
              </div>
              {resendMessage && (
                <div className={`text-sm ${resendMessage.includes('sent') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {resendMessage}
                </div>
              )}
              <div className="space-y-2">
                <button
                  onClick={handleClose}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                >
                  Close
                </button>
                <button
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="w-full text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {resendLoading ? 'Sending...' : "Didn't receive it? Resend email"}
                </button>
                <button
                  onClick={() => setStep('email')}
                  className="w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 py-2"
                >
                  Already verified? Log in
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailFirstAuthModal;
